import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { Network } from "@capacitor/network";
import type { PacoteServicoOutput } from "@repo/graphql-types/graphql";
import { getAppInfo } from "@/lib/utils/app-info";
import { logError } from "@/lib/utils/log-error";

interface DeviceData {
  app: {
    build: string;
    name: string;
    namespace: string;
    version: string;
  };
  device: {
    manufacturer: string;
    model: string;
    name?: string;
  };
  network: {
    wifi: boolean;
  };
  os: {
    name: string;
    version: string;
  };
}

async function getDeviceData(): Promise<DeviceData> {
  const appInfo = await getAppInfo();
  const deviceInfo = await Device.getInfo();
  const networkStatus = await Network.getStatus();

  return {
    app: {
      build: appInfo.build,
      name: appInfo.name,
      namespace: appInfo.id,
      version: appInfo.version,
    },
    device: {
      manufacturer: deviceInfo.manufacturer,
      model: deviceInfo.model,
      name: deviceInfo.name,
    },
    network: {
      wifi: networkStatus.connectionType === "wifi",
    },
    os: {
      name: deviceInfo.operatingSystem,
      version: deviceInfo.osVersion,
    },
  };
}

/**
 * Tempo máximo de espera pela coleta de contexto de dispositivo. `getDeviceData`
 * depende de plugins Capacitor que, em ROM/OS específicos, podem ficar pendentes
 * sem resolver nem rejeitar — o timeout garante que isso não segure o disparo do
 * evento indefinidamente. Ver CBR-3937.
 */
const DEVICE_DATA_TIMEOUT_MS = 3000;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`getDeviceData excedeu ${ms}ms`));
    }, ms);
  });

  return Promise.race([promise, timeout]).finally(() => {
    clearTimeout(timeoutId);
  });
};

/**
 * Coleta o contexto de dispositivo de forma best-effort. `deviceData` é
 * enriquecimento, não pré-requisito: qualquer falha (plugin rejeitado ou travado)
 * é registrada no Sentry — com tag dedicada e sem PII, para medir o alcance real
 * do problema — e resulta em `undefined`, para que o evento seja disparado mesmo
 * assim. Antes desta mudança, uma falha aqui derrubava silenciosamente todo o
 * tracking do dispositivo. Ver CBR-3937.
 */
const collectDeviceData = async (): Promise<DeviceData | undefined> => {
  try {
    return await withTimeout(getDeviceData(), DEVICE_DATA_TIMEOUT_MS);
  } catch (error) {
    logError(error, { tags: { "tracking.device_data_failed": "true" } });
    return undefined;
  }
};

/**
 * Coleta memoizada por sessão. `deviceData` é praticamente estático (app/os/device
 * não mudam durante a sessão; apenas `network.wifi` poderia variar), então coletamos
 * uma única vez e reusamos a mesma Promise. Isso evita chamar os plugins Capacitor a
 * cada evento e, num device com o plugin quebrado, colapsa o timeout e o log de falha
 * do Sentry para uma única ocorrência por sessão — em vez de um por evento. Ver CBR-3937.
 */
let deviceDataPromise: Promise<DeviceData | undefined> | undefined;

const getDeviceDataSafe = (): Promise<DeviceData | undefined> => {
  deviceDataPromise ??= collectDeviceData();
  return deviceDataPromise;
};

/** Reseta o cache de `deviceData` da sessão. Usado nos testes para isolar cenários. */
export const resetDeviceDataCache = (): void => {
  deviceDataPromise = undefined;
};

interface PageViewProps {
  from: string;
}

export const pageView = async ({ from }: PageViewProps): Promise<void> => {
  const deviceData = await getDeviceDataSafe();

  window.rudderanalytics?.page(
    undefined,
    undefined,
    {
      platform: Capacitor.getPlatform(),
      from,
    },
    {
      ...deviceData,
    },
  );
};

export const trackIdentify = (
  codUsuario: number,
  nome?: string,
  sobrenome?: string,
  email?: string,
  apelido?: string | null,
  codClinica?: number,
  pacotesServicos?: PacoteServicoOutput[],
): void => {
  void getDeviceDataSafe().then((deviceData) => {
    window.rudderanalytics?.identify(String(codUsuario), {
      name: apelido ?? `${nome} ${sobrenome}`,
      email,
      codUsuario,
      codClinica,
      pacotesServicos,
      ...deviceData,
    });
  });
};

export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  void getDeviceDataSafe().then((deviceData) => {
    window.rudderanalytics?.track(eventName, { ...deviceData, ...properties });
  });
};

/**
 * Tempo máximo para o SDK do RudderStack carregar e inicializar. Passado esse
 * limite sem o `ready`, tratamos como SDK não carregado — cenário típico de CDN
 * (`cdn.rudderlabs.com`) ou data plane bloqueados por DNS privado / adblocker /
 * VPN no device. Ver CBR-3937.
 */
const SDK_LOAD_TIMEOUT_MS = 10000;

/**
 * Verifica, uma vez por sessão, se o SDK do RudderStack carregou. Deve ser chamada
 * no boot do app. Se o `ready` não disparar dentro do timeout, registra um único
 * log no Sentry (tag dedicada, sem PII) — tornando visível a perda de tracking por
 * bloqueio de rede, que de outra forma é silenciosa (`window.rudderanalytics?.track`
 * vira no-op quando o SDK não está presente). O Sentry não é classificado como
 * tracker, então o log chega mesmo nos devices onde o RudderStack é bloqueado.
 */
export const monitorRudderStackLoad = (): void => {
  let ready = false;
  window.rudderanalytics?.ready(() => {
    ready = true;
  });

  setTimeout(() => {
    if (!ready) {
      logError(new Error("RudderStack SDK não carregou"), {
        tags: { "tracking.sdk_not_loaded": "true" },
      });
    }
  }, SDK_LOAD_TIMEOUT_MS);
};
